import { useEffect } from "react";
import {
  activities as activitiesApi,
  progress as progressApi,
  assessment as assessmentApi,
  payments,
  courses as coursesApi,
  notifications as notificationsApi,
} from "@/lib/api";
import { prefetchAll } from "@/lib/cache";

// Nav-page JS chunks to preload once the user is authenticated.
// Calling import() here causes webpack to fetch+cache the chunk in the
// background so every first navigation is instant (no spinner).
const NAV_CHUNKS = [
  () => import("@/pages/Home"),
  () => import("@/pages/Activities"),
  () => import("@/pages/Report"),
  () => import("@/pages/Progress"),
  () => import("@/pages/Courses"),
  () => import("@/pages/More"),
  () => import("@/pages/Settings"),
  () => import("@/pages/Disa"),
  () => import("@/pages/Notifications"),
];

function preloadNavChunks() {
  const schedule = typeof requestIdleCallback === "function" ? requestIdleCallback : (cb) => setTimeout(cb, 200);
  schedule(() => { NAV_CHUNKS.forEach((load) => load().catch(() => {})); });
}

/**
 * Warm the dashboard SWR cache on first mount + on every PWA resume so
 * Today / Progress / Report / Settings render instantly from the cache on
 * first navigation. All pages still revalidate in the background — so
 * cached + fresh stay in sync. Failures are swallowed.
 *
 * The `visibilitychange` re-trigger is the key bit for mobile PWAs
 * (iOS + Android): when the parent resumes Kiddo+ after a few hours or
 * days, the in-memory cache is gone (Safari/Chrome may have killed the
 * webview) and the sessionStorage cache may be stale. Firing the same
 * prefetch on resume makes the dashboard self-heal silently.
 *
 * Extracted from `AppShell` to keep that component focused on layout.
 */
export default function useDashboardPrefetch(userId) {
  useEffect(() => {
    if (!userId) return undefined;

    // Preload JS chunks for all nav pages on first auth — once is enough.
    preloadNavChunks();

    const prefetchDashboard = () => {
      prefetchAll([
        { key: "activities:daily",  fetcher: () => activitiesApi.daily() },
        { key: "progress:streak",   fetcher: () => progressApi.streak() },
        { key: "progress:compare",  fetcher: () => progressApi.compare() },
        { key: "progress:insights", fetcher: () => progressApi.insights() },
        { key: "report:latest",     fetcher: () => assessmentApi.latest() },
        { key: "payments:history",  fetcher: () => payments.history() },
        { key: "payments:methods",  fetcher: () => payments.methods() },
        { key: "payments:subscription", fetcher: () => payments.subscription() },
        { key: "activities:library", fetcher: () => activitiesApi.library() },
        { key: "activities:recent",  fetcher: () => activitiesApi.recent() },
        { key: "courses:list",       fetcher: () => coursesApi.list() },
        { key: "notifications:inbox", fetcher: () => notificationsApi.inbox() },
      ]);
      // Second-pass prefetch: once today's daily list lands, warm the
      // per-id activity detail cache so tapping any card opens the
      // detail page instantly. Failures stay silent.
      activitiesApi.daily()
        .then((d) => {
          const ids = (d?.daily || []).map((a) => a.id).filter(Boolean);
          return prefetchAll(
            ids.map((id) => ({
              key: `activities:detail:${id}`,
              fetcher: () => activitiesApi.detail(id),
            })),
          );
        })
        .catch(() => {});
    };

    prefetchDashboard();

    const onVisible = () => {
      if (document.visibilityState === "visible") prefetchDashboard();
    };
    document.addEventListener("visibilitychange", onVisible);
    // Some Android Chrome PWAs only fire `pageshow` on resume; iOS fires
    // both. Listening to both covers the matrix without double-firing
    // (visibilityState is already 'visible' on initial mount, and the
    // prefetch is idempotent anyway).
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [userId]);
}
